import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Checkbox, Paper, TableContainer } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import SearchIcon from '@mui/icons-material/Search'
import { useSetAtom } from 'jotai'
import DropDownOA from '../../../common/dropDownOA'
import { Item } from '../../../common/snippets'
import TableCellTooltip from '../../../common/TableCellTooltip'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import { GetRequest, PostRequest } from '../../../common/api'
import { urls } from '../../../common/constants'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import useToken from '../../../helpers/useTokens'
import { useTranslation } from '../../../../i18n/I18nProvider'

const emptyShare = () => ({
  active: false,
  branchwise: false,
  vehiclewise: false,
  isOwnFranchisee: false,
  sharing: '0.00',
})

const isChecked = (value) => Number(value) === 1

const shareFromRow = (row) => {
  const amount = Number(row?.SharingPercentage)
  return {
    active: isChecked(row?.IsCompanyActive),
    branchwise: isChecked(row?.HasBranchwise),
    vehiclewise: isChecked(row?.HasVehiclewise),
    isOwnFranchisee: isChecked(row?.IsOwnFranchisee),
    sharing: Number.isFinite(amount) ? amount.toFixed(2) : '0.00',
  }
}

const sortByBranchName = (list) => [...list].sort((a, b) =>
  String(a.BranchName ?? '').localeCompare(String(b.BranchName ?? ''))
)

const branchChecksForCompany = (rows, companyId, branchIdField) => {
  const checks = {}
  rows.forEach((row) => {
    if (String(row.CompanyID) !== String(companyId)) return
    const id = row[branchIdField]
    if (id == null || String(id).trim() === '') return
    checks[String(id).trim()] = true
  })
  return checks
}

const parentChecksForCompany = (rows, companyId) => branchChecksForCompany(rows, companyId, 'ParentBranchID')

const childChecksForCompany = (rows, companyId) => branchChecksForCompany(rows, companyId, 'ChildBranchID')

const vehicleChecksForCompany = (rows, companyId) => branchChecksForCompany(rows, companyId, 'BusID')

const cargoVehicles = (list) => list
  .filter((row) => Number(row.IsActive) === 1 && (row.BusOrCargo === 'X' || row.BusOrCargo === 'C'))
  .sort((a, b) => String(a.BusNumber ?? '').localeCompare(String(b.BusNumber ?? '')))

const headerCellSx = {
  backgroundColor: '#DAEDFE',
  color: '#333',
  fontSize: '12px',
  fontWeight: 600,
  fontFamily: 'Roboto',
  padding: '6px 8px',
  borderRight: '1px solid #e3e3e3',
  borderBottom: '1px solid #d5e6f5',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  verticalAlign: 'top',
}

const bodyCellSx = {
  fontSize: '12px',
  fontFamily: 'Roboto',
  color: '#444',
  padding: '4px 8px',
  borderRight: '1px solid #eee',
  borderBottom: '1px solid #eee',
  whiteSpace: 'nowrap',
}

const CargoSharing = () => {
  const { translations } = useTranslation()
  const mtpl = translations?.mtplMaster || {}
  const { loginData } = useToken()
  const setLoader = useSetAtom(globalLoaderAtom)
  const setAlert = useSetAtom(alertAtom)
  const [companyList, setCompanyList] = useState([])
  const [sharingList, setSharingList] = useState([])
  const [sharingList2, setSharingList2] = useState([])
  const [cargoCompaniesWithBranch, setCargoCompaniesWithBranch] = useState([])
  const [selectedCompany, setSelectedCompany] = useState()
  const [parentBranchList, setParentBranchList] = useState([])
  const [parentBranchChecks, setParentBranchChecks] = useState({})
  const [childBranchList, setChildBranchList] = useState([])
  const [childBranchChecks, setChildBranchChecks] = useState({})
  const [branchListsVisible, setBranchListsVisible] = useState(false)
  const [vehicleList, setVehicleList] = useState([])
  const [vehicleChecks, setVehicleChecks] = useState({})
  const [vehicleListVisible, setVehicleListVisible] = useState(false)
  const childRequestGen = useRef(0)
  const vehicleRequestGen = useRef(0)
  const [shareRows, setShareRows] = useState({})
  const [companyIdSearch, setCompanyIdSearch] = useState('')
  const [companyNameSearch, setCompanyNameSearch] = useState('')
  const [selectedRowId, setSelectedRowId] = useState(null)

  useEffect(() => {
    if (!loginData?.intCompanyID) return
    setLoader(true)
    PostRequest(urls.getCompaniesListAll, {
      intCompanyID: -1,
      intCityID: 0,
      intStateID: 0,
      intIsActive: 1,
    })
      .then((res) => {
        const list = Array.isArray(res?.Data) ? res.Data : res?.Data?.Table
        if (res.status !== 'Success' || !Array.isArray(list)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        const updatedList = [...list]
        .filter((company) => company.canShareCargo === 1)      //filter the companies that can share cargo
        .sort((a, b) => Number(a.CompanyID) - Number(b.CompanyID))  //confirm if we sort it by companyID
        setCompanyList(updatedList)
        const defaultCompany = updatedList.find((company) => company.CompanyID === loginData.intCompanyID) || updatedList[0]
        setSelectedCompany(defaultCompany)
      })
      .catch((err) => {
        setCompanyList([])
        setSelectedCompany()
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }, [loginData?.intCompanyID])

  const loadCargoSharing = () => (
    GetRequest(urls.getCargoSharingData, {
      intUserID: loginData.intUserID,
      intCompanyID: loginData.intCompanyID,
      intIsActive: 1,
    })
      .then((res) => {
        const list = res?.Data?.Table
        if (res.status !== 'Success' || !Array.isArray(list)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        setCargoCompaniesWithBranch(list)
        const seenCompanyIds = new Set()
        const updatedList = [...list]
          .sort((a, b) => String(a.CompanyName ?? '').localeCompare(String(b.CompanyName ?? '')))
          .filter((company) => {
            if (seenCompanyIds.has(company.CompanyID)) return false
            seenCompanyIds.add(company.CompanyID)
            return true
          })
        setSharingList(updatedList)
        setSharingList2(Array.isArray(res?.Data?.Table1) ? res.Data.Table1 : [])
        const nextShare = {}
        updatedList.forEach((company) => {
          nextShare[company.CompanyID] = shareFromRow(company)
        })
        setShareRows(nextShare)
      })
  )

  useEffect(() => {
    if (!companyList.length || !loginData?.intCompanyID || !loginData?.intUserID) return
    setLoader(true)
    loadCargoSharing()
      .catch((err) => {
        setSharingList([])
        setSharingList2([])
        setCargoCompaniesWithBranch([])
        setShareRows({})
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }, [companyList, loginData?.intCompanyID, loginData?.intUserID])

  const loadParentBranches = (companyId) => (
    PostRequest(urls.getBranchConfigOrEwbillinfo, {
      intUserID: 0,
      intCompanyID: companyId,
      intBranchID: 0,
      strBranchName: '',
      strBranchCode: '',
      intStateID: 0,
      intCityID: 0,
      intIsActive: 1,
    })
      .then((res) => {
        const list = res?.Data?.Table
        if (res.status !== 'Success' || !Array.isArray(list)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        setParentBranchList(sortByBranchName(list))
        setParentBranchChecks({})
      })
      .catch((err) => {
        setParentBranchList([])
        setParentBranchChecks({})
        throw err
      })
  )

  useEffect(() => {
    if (!selectedCompany?.CompanyID) {
      setParentBranchList([])
      setParentBranchChecks({})
      return
    }
    setLoader(true)
    loadParentBranches(selectedCompany.CompanyID)
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }, [selectedCompany?.CompanyID])

  const tableRows = useMemo(() => {
    const idQuery = companyIdSearch.trim().toLowerCase()
    const nameQuery = companyNameSearch.trim().toLowerCase()
    return sharingList.filter((company) => {
      if (selectedCompany?.CompanyID && company.CompanyID === selectedCompany.CompanyID) return false
      const idText = String(company.CompanyID ?? '').toLowerCase()
      const nameText = String(company.CompanyName ?? '').toLowerCase()
      if (idQuery && !idText.includes(idQuery)) return false
      if (nameQuery && !nameText.includes(nameQuery)) return false
      return true
    })
  }, [sharingList, selectedCompany?.CompanyID, companyIdSearch, companyNameSearch])

  const updateShare = (companyId, field, value) => {
    setShareRows((prev) => ({
      ...prev,
      [companyId]: {
        ...(prev[companyId] || emptyShare()),
        [field]: value,
      },
    }))
  }

  const fetchChildBranches = (companyId, generation, showLists, setFlag = true) => (
    PostRequest(urls.getBranchConfigOrEwbillinfo, {
      intUserID: 0,
      intCompanyID: companyId,
      intBranchID: 0,
      strBranchName: '',
      strBranchCode: '',
      intStateID: 0,
      intCityID: 0,
      intIsActive: 1,
    })
      .then((res) => {
        if (generation !== childRequestGen.current) return
        const list = res?.Data?.Table
        if (res.status !== 'Success' || !Array.isArray(list)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        if (setFlag) updateShare(companyId, 'branchwise', true)
        if (showLists) {
          setSelectedRowId(companyId)
          setChildBranchList(sortByBranchName(list))
          setChildBranchChecks(childChecksForCompany(cargoCompaniesWithBranch, companyId))
          setParentBranchChecks(parentChecksForCompany(cargoCompaniesWithBranch, companyId))
          setBranchListsVisible(true)
        }
      })
      .catch((err) => {
        if (generation !== childRequestGen.current) return
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
  )

  const handleBranchwiseChange = (companyId, checked) => {
    if (!checked) {
      updateShare(companyId, 'branchwise', false)
      return
    }
    const share = shareRows[companyId] || emptyShare()
    if (!share.active || share.branchwise) return
    updateShare(companyId, 'branchwise', true)
  }

  const fetchVehicles = (companyId, generation, showLists, setFlag = true) => (
    GetRequest(urls.getBusesListAll, {
      intUserID: 0,
      intCompanyID: companyId,
      intBusID: 0,
      intCompanyChartID: 0,
      intProprietorID: 0,
      intBusStatus: 1,
    })
      .then((res) => {
        if (generation !== vehicleRequestGen.current) return
        const list = Array.isArray(res?.Data) ? res.Data : res?.Data?.Table
        if (res.status !== 'Success' || !Array.isArray(list)) {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        if (setFlag) updateShare(companyId, 'vehiclewise', true)
        if (showLists) {
          setSelectedRowId(companyId)
          setVehicleList(cargoVehicles(list))
          setVehicleChecks(vehicleChecksForCompany(sharingList2, companyId))
          setVehicleListVisible(true)
        }
      })
      .catch((err) => {
        if (generation !== vehicleRequestGen.current) return
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
  )

  const handleVehiclewiseChange = (companyId, checked) => {
    if (!checked) {
      updateShare(companyId, 'vehiclewise', false)
      return
    }
    const share = shareRows[companyId] || emptyShare()
    if (!share.active || share.vehiclewise) return
    updateShare(companyId, 'vehiclewise', true)
  }

  const selectSharingRow = (companyId) => {
    setSelectedRowId(companyId)
    setParentBranchChecks(parentChecksForCompany(cargoCompaniesWithBranch, companyId))
    setBranchListsVisible(true)
    setChildBranchList([])
    setVehicleList([])
    setVehicleChecks({})
    const childGeneration = ++childRequestGen.current
    const vehicleGeneration = ++vehicleRequestGen.current
    setLoader(true)
    Promise.all([
      fetchChildBranches(companyId, childGeneration, true, false),
      fetchVehicles(companyId, vehicleGeneration, true, false),
    ]).finally(() => {
      if (childGeneration === childRequestGen.current && vehicleGeneration === vehicleRequestGen.current) {
        setLoader(false)
      }
    })
  }

  const handleSharingChange = (companyId, value) => {
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return
    updateShare(companyId, 'sharing', value)
  }

  const handleSharingBlur = (companyId, value) => {
    const amount = Number(value)
    updateShare(companyId, 'sharing', Number.isFinite(amount) ? amount.toFixed(2) : '0.00')
  }

  const joinedCheckedIds = (items, checks, idKey) => items
    .filter((item) => checks?.[item[idKey]])
    .map((item) => item[idKey])
    .join('^')

  const handleSave = () => {
    if (!selectedCompany?.CompanyID) {
      setAlert({
        show: true,
        msg: mtpl.pleaseSelectCompany || 'Please select company',
        nextId: '',
      })
      return
    }
    if (!selectedRowId) {
      setAlert({
        show: true,
        msg: mtpl.pleaseSelectCompanyRow || 'Please select a company row',
        nextId: '',
      })
      return
    }
    const activeCompanies = sharingList.filter((company) =>
      String(company.CompanyID) !== String(selectedCompany.CompanyID) && shareRows[company.CompanyID]?.active
    )
    const flagValue = (company, field) => (shareRows[company.CompanyID]?.[field] ? '1' : '0')
    const commPer = (company) => {
      const amount = Number(shareRows[company.CompanyID]?.sharing)
      return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
    }
    const payload = {
      SharedCompanyID: selectedCompany.CompanyID,
      SharedWithCompanyIDs: activeCompanies.map((company) => company.CompanyID).join('^'),
      HasBranchwise: activeCompanies.map((company) => flagValue(company, 'branchwise')).join('^'),
      HasVehiclewise: activeCompanies.map((company) => flagValue(company, 'vehiclewise')).join('^'),
      IsOwnFranchisee: activeCompanies.map((company) => flagValue(company, 'isOwnFranchisee')).join('^'),
      CommPer: activeCompanies.map(commPer).join('^'),
      ParentCompanyIDForBranchSharing: selectedCompany.CompanyID,
      ChildCompanyIDForBranchSharing: selectedRowId,
      ParentBranchIDs: joinedCheckedIds(parentBranchList, parentBranchChecks, 'BranchID'),
      ChildBranchIDs: joinedCheckedIds(childBranchList, childBranchChecks, 'BranchID'),
      ParentCompanyIDForVehicleSharing: selectedCompany.CompanyID,
      ChildCompanyIDForVehicleSharing: selectedRowId,
      VehicleIDs: joinedCheckedIds(vehicleList, vehicleChecks, 'BusID'),
    }
    setLoader(true)
    PostRequest(urls.CargoCompanySharingInsertUpdate, payload)
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res?.ErrMessage || mtpl.invalidResponse || 'Invalid Response')
        }
        setChildBranchList([])
        setVehicleList([])
        setChildBranchChecks({})
        setVehicleChecks({})
        setBranchListsVisible(false)
        setVehicleListVisible(false)
        return Promise.all([
          loadCargoSharing(),
          loadParentBranches(selectedCompany.CompanyID),
        ]).then(() => {
          setSelectedRowId(null)
          setAlert({
            show: true,
            msg: mtpl.cargoSharingSaved || 'Cargo Sharing Details Saved Successfully',
            nextId: '',
          })
        })
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const searchBox = (value, onChange) => (
    <div style={{ position: 'relative', marginTop: '6px' }}>
      <input
        className="field"
        value={value}
        placeholder={mtpl.searchPlaceholder || 'Search...'}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          height: '24px',
          fontSize: '12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '3px 22px 3px 10px',
          boxSizing: 'border-box',
          background: '#fff',
        }}
      />
      <SearchIcon sx={{ position: 'absolute', right: '4px', top: '4px', fontSize: '16px', color: '#777', pointerEvents: 'none' }} />
    </div>
  )
  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', background: '#f7f9fb' }}>
      <Box sx={{ ...sx.reportFlex, marginTop: '10px', paddingBottom: '12px' }} className="reportButton">
        <Item sx={sx.itemsx}>
          <DropDownOA
            data={companyList}
            defaultvalue={selectedCompany?.CompanyName || ''}
            width="100%"
            label={mtpl.company || 'Company'}
            dataKey="CompanyName"
            idKey="CompanyID"
            minWidth="320px"
            onSelResetVal={true}
            onSelect={(company) => setSelectedCompany(company)}
          />
        </Item>
        <Item sx={sx.itemsx} className="disabled-wrapper">
          <Button
            variant="contained"
            color="secondary"
            className="btn btn-primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            style={{ ...sx.fullHeight, ...sx.novp }}
          >
            {mtpl.cargoSharingSave || 'Save'}
          </Button>
        </Item>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: '10px', padding: '0 22px 12px' }}>
        <TableContainer component={Paper} className="scrollBlue" sx={{ flex: 1.35, minWidth: 0, borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...headerCellSx, minWidth: '130px', textAlign: 'left' }}>
                  {mtpl.crsCompanyId || 'CRS Company Id'}
                  {searchBox(companyIdSearch, setCompanyIdSearch)}
                </th>
                <th style={{ ...headerCellSx, minWidth: '160px', textAlign: 'left' }}>
                  {mtpl.companyName || 'Company Name'}
                  {searchBox(companyNameSearch, setCompanyNameSearch)}
                </th>
                <th style={{ ...headerCellSx, textAlign: 'center' }}>{mtpl.active || 'Active'}</th>
                <th style={{ ...headerCellSx, textAlign: 'center' }}>{mtpl.branchwise || 'Branchwise'}</th>
                <th style={{ ...headerCellSx, textAlign: 'center' }}>{mtpl.vehiclewise || 'Vehiclewise'}</th>
                <th style={{ ...headerCellSx, textAlign: 'center' }}>{mtpl.isOwnFranchisee || 'IsOwnFranchisee'}</th>
                <th style={{ ...headerCellSx, textAlign: 'right', borderRight: 'none' }}>{mtpl.sharingPercent || 'Sharing%'}</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((company) => {
                const share = shareRows[company.CompanyID] || emptyShare()
                const selected = selectedRowId === company.CompanyID
                return (
                  <tr
                    key={company.CompanyID}
                    onClick={() => selectSharingRow(company.CompanyID)}
                    style={{ background: selected ? '#d6ebfa' : '#fff', cursor: 'pointer' }}
                  >
                    <td style={bodyCellSx}>{company.CompanyID}</td>
                    <TableCellTooltip
                      title={company.CompanyName}
                      data={company.CompanyName}
                      placement="bottom-start"
                      textAlign="left"
                      maxWidth="180px"
                      style={bodyCellSx}
                    />
                    {['active', 'branchwise', 'vehiclewise', 'isOwnFranchisee'].map((field) => (
                      <td key={field} style={{ ...bodyCellSx, textAlign: 'center' }}>
                        <Checkbox
                          size="small"
                          checked={!!share[field]}
                          onClick={(e) => {
                            if (share[field]) e.stopPropagation()
                          }}
                          onChange={(e) => {
                            if (field === 'branchwise') handleBranchwiseChange(company.CompanyID, e.target.checked)
                            else if (field === 'vehiclewise') handleVehiclewiseChange(company.CompanyID, e.target.checked)
                            else updateShare(company.CompanyID, field, e.target.checked)
                          }}
                          sx={{ padding: 0 }}
                        />
                      </td>
                    ))}
                    <td style={{ ...bodyCellSx, textAlign: 'right', borderRight: 'none' }}>
                      <input
                        value={share.sharing}
                        onMouseDown={(e) => {
                          e.currentTarget.dataset.wasFocused = document.activeElement === e.currentTarget ? '1' : ''
                        }}
                        onClick={(e) => {
                          if (e.currentTarget.dataset.wasFocused) e.stopPropagation()
                        }}
                        onChange={(e) => handleSharingChange(company.CompanyID, e.target.value)}
                        onBlur={(e) => handleSharingBlur(company.CompanyID, e.target.value)}
                        style={{
                          width: '64px',
                          textAlign: 'right',
                          border: '1px solid #cfcfcf',
                          fontSize: '12px',
                          padding: '2px 4px',
                        }}
                      />
                    </td>
                  </tr>
                )
              })}
              {!tableRows.length && (
                <tr>
                  <td colSpan={7} style={{ ...bodyCellSx, textAlign: 'center', padding: '16px' }}>
                    {mtpl.noDataFound || 'No Data Found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableContainer>
        {[
          { title: mtpl.childBranches || 'Child Branches', items: branchListsVisible ? childBranchList : [], checks: childBranchChecks, setChecks: setChildBranchChecks, idKey: 'BranchID', nameKey: 'BranchName' },
          { title: mtpl.parentBranches || 'Parent Branches', items: branchListsVisible ? parentBranchList : [], checks: parentBranchChecks, setChecks: setParentBranchChecks, idKey: 'BranchID', nameKey: 'BranchName' },
          { title: mtpl.vehicles || 'Vehicles', items: vehicleListVisible ? vehicleList : [], checks: vehicleChecks, setChecks: setVehicleChecks, idKey: 'BusID', nameKey: 'BusNumber' },
        ].map(({ title, items, checks, setChecks, idKey, nameKey }) => (
          <Paper key={title} sx={{ flex: 0.7, minWidth: 0, borderRadius: '4px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ background: '#DAEDFE', textAlign: 'center', fontSize: '13px', fontWeight: 600, fontFamily: 'Roboto', color: '#333', padding: '8px' }}>
              {title}
            </Box>
            <Box className="scrollBlue" sx={{ flex: 1, overflow: 'auto' }}>
              {items.map((item) => (
                <Box
                  key={item[idKey]}
                  sx={{ display: 'flex', alignItems: 'center', fontSize: '12px', fontFamily: 'Roboto', color: '#444', padding: '4px 8px', borderBottom: '1px solid #eee' }}
                >
                  {setChecks && (
                    <Checkbox
                      size="small"
                      checked={!!checks?.[item[idKey]]}
                      onChange={(e) => setChecks((prev) => ({ ...prev, [item[idKey]]: e.target.checked }))}
                      sx={{ padding: 0, marginRight: '12px' }}
                    />
                  )}
                  {item[nameKey]}
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  )
}

export default CargoSharing
